import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import {
  Badge,
  Box,
  Button,
  Card,
  Flex,
  Heading,
  SegmentedControl,
  Table,
  Text,
} from "@radix-ui/themes";

import { fetchActivitySummary, type ActivityEvent } from "../api/activity";
import { authClient } from "../lib/auth";
import { ThreadLogo } from "../components/ThreadLogo";

const DAY_OPTIONS = [1, 7, 30, 90];

function isFlexcomputeEmail(email?: string | null): boolean {
  return !!email?.toLowerCase().endsWith("@flexcompute.com");
}

function formatDateTime(iso?: string | null): string {
  if (!iso) return "Never";
  return new Date(iso).toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function timeAgo(iso?: string | null): string {
  if (!iso) return "never";
  const seconds = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
  if (seconds < 60) return "just now";
  if (seconds < 3600) return `${Math.floor(seconds / 60)} min ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)} hours ago`;
  return `${Math.floor(seconds / 86400)} days ago`;
}

function viewerLabel(event: ActivityEvent): string {
  if (event.email) return event.email;
  if (event.client_host_hash) return `anonymous:${event.client_host_hash.slice(0, 8)}`;
  return "unknown";
}

function eventColor(eventType: string): "green" | "blue" | "orange" | "gray" {
  if (eventType === "page_view") return "green";
  if (eventType.includes("email_auth")) return "blue";
  if (eventType.includes("flow360")) return "orange";
  return "gray";
}

function displayPath(path?: string | null, slug?: string | null): string {
  if (path) return path;
  return slug ? `/presentations/${slug}` : "No page path";
}

function MetricCard({
  label,
  value,
  detail,
}: {
  label: string;
  value: string | number;
  detail: string;
}) {
  return (
    <Card>
      <Flex direction="column" gap="1">
        <Text size="1" color="gray" weight="medium">
          {label}
        </Text>
        <Text size="7" weight="bold">
          {value}
        </Text>
        <Text size="1" color="gray">
          {detail}
        </Text>
      </Flex>
    </Card>
  );
}

export default function ActivityDashboard() {
  const [days, setDays] = useState(30);
  const sessionQuery = useQuery({
    queryKey: ["current-session"],
    queryFn: async () => (await authClient.auth.getSession()).data.session,
  });

  const email = sessionQuery.data?.user.email;
  const staff = isFlexcomputeEmail(email);

  const summaryQuery = useQuery({
    queryKey: ["activity-summary", days],
    queryFn: () => fetchActivitySummary(days),
    enabled: staff,
    refetchInterval: 60_000,
  });

  const sortedPages = useMemo(
    () => [...(summaryQuery.data?.pages ?? [])].sort((a, b) => b.views - a.views),
    [summaryQuery.data?.pages],
  );

  if (sessionQuery.isLoading) {
    return (
      <Box p="6">
        <Text color="gray">Loading activity access...</Text>
      </Box>
    );
  }

  if (!staff) {
    return (
      <Box
        p="6"
        style={{
          minHeight: "100vh",
          background: "#0b0f0d",
          color: "#eef2ef",
        }}
      >
        <Flex direction="column" gap="4" style={{ maxWidth: 680 }}>
          <ThreadLogo height={28} />
          <Heading size="6">Flexcompute Only</Heading>
          <Text color="gray">
            Activity metrics include customer access data, so this page is gated to
            Flexcompute accounts.
          </Text>
          <Link to="/">
            <Button variant="soft">Back to projects</Button>
          </Link>
        </Flex>
      </Box>
    );
  }

  const summary = summaryQuery.data;
  const overview = summary?.overview;

  return (
    <Box
      p={{ initial: "4", md: "6" }}
      style={{
        minHeight: "100vh",
        background: "#0b0f0d",
        color: "#eef2ef",
      }}
    >
      <Flex direction="column" gap="5" style={{ maxWidth: 1320, margin: "0 auto" }}>
        <Flex align="center" justify="between" gap="4" wrap="wrap">
          <Flex align="center" gap="4">
            <ThreadLogo height={28} />
            <Box>
              <Heading size="6">Activity</Heading>
              <Text size="2" color="gray">
                Page views, presentation access, and sign-in events across Thread.
              </Text>
            </Box>
          </Flex>

          <Flex align="center" gap="3" wrap="wrap">
            <SegmentedControl.Root
              value={String(days)}
              onValueChange={(value) => setDays(Number(value))}
              size="2"
            >
              {DAY_OPTIONS.map((option) => (
                <SegmentedControl.Item key={option} value={String(option)}>
                  {option}d
                </SegmentedControl.Item>
              ))}
            </SegmentedControl.Root>
            <Button
              variant="soft"
              onClick={() => summaryQuery.refetch()}
              disabled={summaryQuery.isFetching}
            >
              {summaryQuery.isFetching ? "Refreshing..." : "Refresh"}
            </Button>
            <Link to="/">
              <Button variant="ghost">Projects</Button>
            </Link>
          </Flex>
        </Flex>

        {summaryQuery.isError ? (
          <Card>
            <Text color="red">
              Could not load activity. The API will only return this data to
              Flexcompute staff.
            </Text>
          </Card>
        ) : null}

        <Box
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))",
            gap: 12,
          }}
        >
          <MetricCard
            label="Page Views"
            value={overview?.page_views ?? 0}
            detail={`Across the last ${days} days`}
          />
          <MetricCard
            label="Known Viewers"
            value={overview?.known_viewers ?? 0}
            detail="Signed-in or email-code visitors"
          />
          <MetricCard
            label="Anonymous Clients"
            value={overview?.anonymous_clients ?? 0}
            detail="Grouped by hashed client host"
          />
          <MetricCard
            label="Last Event"
            value={overview?.last_seen ? timeAgo(overview.last_seen) : "None"}
            detail={formatDateTime(overview?.last_seen)}
          />
        </Box>

        <Box
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 420px), 1fr))",
            gap: 16,
            alignItems: "start",
          }}
        >
          <Card style={{ overflowX: "auto" }}>
            <Flex justify="between" align="center" mb="3">
              <Heading size="4">Pages</Heading>
              <Text size="1" color="gray">
                {sortedPages.length} paths
              </Text>
            </Flex>
            <Table.Root variant="surface">
              <Table.Header>
                <Table.Row>
                  <Table.ColumnHeaderCell>Page</Table.ColumnHeaderCell>
                  <Table.ColumnHeaderCell>Views</Table.ColumnHeaderCell>
                  <Table.ColumnHeaderCell>Known</Table.ColumnHeaderCell>
                  <Table.ColumnHeaderCell>Anon</Table.ColumnHeaderCell>
                  <Table.ColumnHeaderCell>Last Seen</Table.ColumnHeaderCell>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {sortedPages.map((page) => (
                  <Table.Row key={`${page.path}:${page.presentation_slug ?? ""}`}>
                    <Table.Cell>
                      <Flex direction="column" gap="1">
                        <Text size="2" weight="medium">
                          {displayPath(page.path, page.presentation_slug)}
                        </Text>
                        {page.presentation_slug ? (
                          <Text size="1" color="gray">
                            presentation: {page.presentation_slug}
                          </Text>
                        ) : null}
                      </Flex>
                    </Table.Cell>
                    <Table.Cell>{page.views}</Table.Cell>
                    <Table.Cell>{page.known_viewers}</Table.Cell>
                    <Table.Cell>{page.anonymous_clients}</Table.Cell>
                    <Table.Cell>
                      <Text size="2">{timeAgo(page.last_seen)}</Text>
                    </Table.Cell>
                  </Table.Row>
                ))}
                {!summaryQuery.isLoading && sortedPages.length === 0 ? (
                  <Table.Row>
                    <Table.Cell colSpan={5}>
                      <Text color="gray">No page views in this window.</Text>
                    </Table.Cell>
                  </Table.Row>
                ) : null}
              </Table.Body>
            </Table.Root>
          </Card>

          <Card style={{ overflowX: "auto" }}>
            <Flex justify="between" align="center" mb="3">
              <Heading size="4">Viewers</Heading>
              <Text size="1" color="gray">
                {summary?.viewers.length ?? 0} identities
              </Text>
            </Flex>
            <Table.Root variant="surface">
              <Table.Header>
                <Table.Row>
                  <Table.ColumnHeaderCell>Viewer</Table.ColumnHeaderCell>
                  <Table.ColumnHeaderCell>Views</Table.ColumnHeaderCell>
                  <Table.ColumnHeaderCell>Pages</Table.ColumnHeaderCell>
                  <Table.ColumnHeaderCell>Last</Table.ColumnHeaderCell>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {(summary?.viewers ?? []).map((viewer) => (
                  <Table.Row key={`${viewer.email ?? "anon"}:${viewer.client_host_hash ?? ""}`}>
                    <Table.Cell>
                      <Text size="2" weight="medium">
                        {viewer.email ?? `anonymous:${viewer.client_host_hash?.slice(0, 8) ?? "unknown"}`}
                      </Text>
                    </Table.Cell>
                    <Table.Cell>{viewer.page_views}</Table.Cell>
                    <Table.Cell>{viewer.pages_seen}</Table.Cell>
                    <Table.Cell>{timeAgo(viewer.last_seen)}</Table.Cell>
                  </Table.Row>
                ))}
                {!summaryQuery.isLoading && !summary?.viewers.length ? (
                  <Table.Row>
                    <Table.Cell colSpan={4}>
                      <Text color="gray">No viewers in this window.</Text>
                    </Table.Cell>
                  </Table.Row>
                ) : null}
              </Table.Body>
            </Table.Root>
          </Card>
        </Box>

        <Card style={{ overflowX: "auto" }}>
          <Flex justify="between" align="center" mb="3">
            <Heading size="4">Recent Events</Heading>
            <Text size="1" color="gray">
              newest first
            </Text>
          </Flex>
          <Table.Root variant="surface">
            <Table.Header>
              <Table.Row>
                <Table.ColumnHeaderCell>Time</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell>Type</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell>Viewer</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell>Page</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell>Auth</Table.ColumnHeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {(summary?.recent_events ?? []).map((event) => (
                <Table.Row key={event.id}>
                  <Table.Cell>
                    <Text size="2">{formatDateTime(event.created_at)}</Text>
                  </Table.Cell>
                  <Table.Cell>
                    <Badge color={eventColor(event.event_type)}>{event.event_type}</Badge>
                  </Table.Cell>
                  <Table.Cell>
                    <Text size="2">{viewerLabel(event)}</Text>
                  </Table.Cell>
                  <Table.Cell>
                    <Text size="2">
                      {displayPath(event.path, event.presentation_slug)}
                    </Text>
                  </Table.Cell>
                  <Table.Cell>
                    <Text size="2" color="gray">
                      {event.auth_method ?? "-"}
                    </Text>
                  </Table.Cell>
                </Table.Row>
              ))}
              {!summaryQuery.isLoading && !summary?.recent_events.length ? (
                <Table.Row>
                  <Table.Cell colSpan={5}>
                    <Text color="gray">No events in this window.</Text>
                  </Table.Cell>
                </Table.Row>
              ) : null}
            </Table.Body>
          </Table.Root>
        </Card>
      </Flex>
    </Box>
  );
}
